import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useIdioma } from './idioma';
import { Icono } from './iconos';

type Tema = 'claro' | 'oscuro';
type Contexto = { tema: Tema; cambiar: () => void };

const Ctx = createContext<Contexto>({ tema: 'claro', cambiar: () => {} });

/**
 * Claro u oscuro, para toda la app. Vive en la raíz y no dentro de una pantalla: si cada pantalla llevara su
 * propio interruptor, cambiarlo en una no cambiaría nada en las demás.
 */
export function ProveedorDeTema({ children }: { children: ReactNode }) {
  const [tema, setTema] = useState<Tema>(() => {
    try {
      const guardado = localStorage.getItem('igor-tema');
      if (guardado === 'claro' || guardado === 'oscuro') return guardado;
    } catch {
      // Navegar en privado o con las cookies bloqueadas no debe dejar la app sin tema.
    }
    return matchMedia('(prefers-color-scheme: dark)').matches ? 'oscuro' : 'claro';
  });

  useEffect(() => {
    // Los estilos usan data-theme="light" | "dark" en la etiqueta <html>.
    document.documentElement.dataset.theme = tema === 'oscuro' ? 'dark' : 'light';
    try {
      localStorage.setItem('igor-tema', tema);
    } catch {
      // Si no se puede guardar, al menos el cambio se ve mientras dure la visita.
    }
  }, [tema]);

  return <Ctx.Provider value={{ tema, cambiar: () => setTema((t) => (t === 'claro' ? 'oscuro' : 'claro')) }}>{children}</Ctx.Provider>;
}

export const useTema = () => useContext(Ctx);

/** El botón, para poder ponerlo en cualquier pantalla sin repetir la lógica. */
export function BotonDeTema() {
  const { tema, cambiar } = useTema();
  const { t } = useIdioma();
  const texto = tema === 'claro' ? t('Modo noche') : t('Modo día');
  return (
    <button className="sel-mes" onClick={cambiar} title={texto} aria-label={texto}>
      <Icono nombre={tema === 'claro' ? 'luna' : 'sol'} />
    </button>
  );
}
